import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { AdminProviderOverviewItem, AdminProviderConfigInput } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { AwsCostsPanel } from './AwsCostsPanel';
import { ProvidersShell } from './ProvidersShell';
import { toUsd, toCredits, fmtUsd, fmtCap } from './credits-format';

function usageTone(pct: number | null, softPercent: number): string {
  if (pct === null) return 'text-muted-foreground';
  if (pct >= 100) return 'text-red-600 dark:text-red-400 font-semibold';
  if (pct >= softPercent) return 'text-amber-600 dark:text-amber-400';
  return 'text-muted-foreground';
}

/**
 * Compact setup indicator: key configured / missing, plus whether live
 * balance fetching exists. Placeholder-valued keys still show ✓ — the
 * detail page's balance refresh is the deeper probe.
 */
function SetupCell({ setup }: { setup: AdminProviderOverviewItem['setup'] }) {
  if (!setup.inferenceKeyConfigured) {
    return (
      <span
        className="text-red-600 dark:text-red-400"
        title="No inference key source configured (Secrets Manager ARN or local env key)"
      >
        ✗ no key
      </span>
    );
  }
  return (
    <span className="text-muted-foreground">
      <span className="text-green-600 dark:text-green-400" title="Inference key configured">
        ✓ key
      </span>
      {setup.managementKeyConfigured === false && (
        <span
          className="ml-2 text-amber-600 dark:text-amber-400"
          title="No admin/management key — live MTD-cost fetch unavailable"
        >
          admin key missing
        </span>
      )}
      {!setup.balanceFetcherAvailable && (
        <span className="ml-2" title="No live balance API — reconcile via manual entries">
          manual balance
        </span>
      )}
    </span>
  );
}

function WindowCell({
  spent,
  reserved,
  cap,
  pct,
  softPercent,
}: {
  spent: number;
  reserved: number;
  cap: number;
  pct: number | null;
  softPercent: number;
}) {
  return (
    <div>
      <span className={usageTone(pct, softPercent)}>
        {fmtUsd(spent)} / {fmtCap(cap)}
        {pct !== null ? ` (${pct.toFixed(0)}%)` : ''}
      </span>
      {reserved > 0 && (
        <span className="text-xs text-muted-foreground"> +{fmtUsd(reserved)} reserved</span>
      )}
    </div>
  );
}

export function ProvidersPage() {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: queryKeys.adminProviders.overview(),
    queryFn: () => api.adminGetProviders(),
  });
  const [editing, setEditing] = useState<AdminProviderOverviewItem | null>(null);

  return (
    <ProvidersShell>
      <p className="text-sm text-muted-foreground mb-6 max-w-3xl">
        Upstream provider spend caps &amp; safety. The per-provider cap is a platform-wide circuit
        breaker — when a provider hits its daily or monthly cap, further calls to it are blocked
        until the window resets or you raise the cap. Spend shown is finalized (plus any in-flight
        reserved). Click a provider for balances, reconciliation, and detail.
      </p>

      <AwsCostsPanel />

      {isError && (
        <p className="text-destructive mb-4">
          {error instanceof Error ? error.message : 'Failed to load providers'}
        </p>
      )}

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="p-3 font-medium">Provider</th>
                <th className="p-3 font-medium">Status</th>
                <th className="p-3 font-medium">Daily (spent / cap)</th>
                <th className="p-3 font-medium">Monthly (spent / cap)</th>
                <th className="p-3 font-medium">Setup</th>
                <th className="p-3 font-medium">Account</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {data?.providers.map((p) => (
                <tr key={p.provider} className="border-b border-border last:border-0">
                  <td className="p-3 font-medium">{p.provider}</td>
                  <td className="p-3">
                    {p.enabled ? (
                      <span className="text-green-600 dark:text-green-400">Active</span>
                    ) : (
                      <span className="text-red-600 dark:text-red-400">Disabled</span>
                    )}
                  </td>
                  <td className="p-3">
                    <WindowCell
                      spent={p.spend.daySpentRawCredits}
                      reserved={p.spend.dayReservedRawCredits}
                      cap={p.caps.dailyCapRawCredits}
                      pct={p.dayUsagePct}
                      softPercent={p.caps.softAlertPercent}
                    />
                  </td>
                  <td className="p-3">
                    <WindowCell
                      spent={p.spend.monthSpentRawCredits}
                      reserved={p.spend.monthReservedRawCredits}
                      cap={p.caps.monthlyCapRawCredits}
                      pct={p.monthUsagePct}
                      softPercent={p.caps.softAlertPercent}
                    />
                  </td>
                  <td className="p-3 text-sm">
                    <SetupCell setup={p.setup} />
                  </td>
                  <td className="p-3 text-muted-foreground">
                    {p.accountLabel ?? (p.hasConfig ? '—' : 'default caps')}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Link
                      to={`/dev-console/providers/${encodeURIComponent(p.provider)}`}
                      className="text-primary hover:underline mr-3"
                    >
                      Details
                    </Link>
                    <button
                      type="button"
                      onClick={() => setEditing(p)}
                      className="text-primary hover:underline"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ProviderConfigEditor
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: queryKeys.adminProviders.all });
          }}
        />
      )}
    </ProvidersShell>
  );
}

function ProviderConfigEditor({
  item,
  onClose,
  onSaved,
}: {
  item: AdminProviderOverviewItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [enabled, setEnabled] = useState(item.enabled);
  const [dailyUsd, setDailyUsd] = useState(String(toUsd(item.caps.dailyCapRawCredits)));
  const [monthlyUsd, setMonthlyUsd] = useState(String(toUsd(item.caps.monthlyCapRawCredits)));
  const [softPercent, setSoftPercent] = useState(String(item.caps.softAlertPercent));
  const [accountLabel, setAccountLabel] = useState(item.accountLabel ?? '');
  const [consoleUrl, setConsoleUrl] = useState(item.consoleUrl ?? '');
  const [billingUrl, setBillingUrl] = useState(item.billingUrl ?? '');
  const [notes, setNotes] = useState(item.notes ?? '');

  const mutation = useMutation({
    mutationFn: (body: AdminProviderConfigInput) =>
      api.adminUpdateProviderConfig(item.provider, body),
    onSuccess: onSaved,
  });

  const handleSave = () => {
    mutation.mutate({
      enabled,
      dailyCapRawCredits: toCredits(Number(dailyUsd) || 0),
      monthlyCapRawCredits: toCredits(Number(monthlyUsd) || 0),
      softAlertPercent: Math.max(1, Math.min(100, Number(softPercent) || 80)),
      accountLabel: accountLabel.trim() || null,
      consoleUrl: consoleUrl.trim() || null,
      billingUrl: billingUrl.trim() || null,
      notes: notes.trim() || null,
    });
  };

  const field =
    'w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-card border border-border rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-1">Edit {item.provider}</h2>
        <p className="text-xs text-muted-foreground mb-4">
          Caps in USD. 0 = unlimited. Disabling blocks all calls to this provider.
        </p>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Cap enforcement enabled
          </label>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Daily cap (USD)</label>
            <input
              type="number"
              aria-label="Daily cap (USD)"
              value={dailyUsd}
              onChange={(e) => setDailyUsd(e.target.value)}
              className={field}
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Monthly cap (USD)</label>
            <input
              type="number"
              aria-label="Monthly cap (USD)"
              value={monthlyUsd}
              onChange={(e) => setMonthlyUsd(e.target.value)}
              className={field}
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Soft alert at (%)</label>
            <input
              type="number"
              aria-label="Soft alert percent"
              value={softPercent}
              onChange={(e) => setSoftPercent(e.target.value)}
              className={field}
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">
              Account label / notes
            </label>
            <input
              type="text"
              placeholder="e.g. Fias Prod — OpenAI org"
              value={accountLabel}
              onChange={(e) => setAccountLabel(e.target.value)}
              className={field}
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Console URL</label>
            <input
              type="url"
              value={consoleUrl}
              onChange={(e) => setConsoleUrl(e.target.value)}
              className={field}
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Billing URL</label>
            <input
              type="url"
              value={billingUrl}
              onChange={(e) => setBillingUrl(e.target.value)}
              className={field}
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">
              Notes (how to find the info)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className={field}
            />
          </div>
        </div>

        {mutation.isError && (
          <p className="text-destructive text-sm mt-3">
            {mutation.error instanceof Error ? mutation.error.message : 'Save failed'}
          </p>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm border border-border rounded-lg"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={mutation.isPending}
            className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
